import { useFormContext, Controller, useWatch } from 'react-hook-form';
import {
  TextField,
  Autocomplete,
  Box,
  InputLabel,
  Typography,
  Paper,
  createFilterOptions,
} from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'src/hooks/use_debounce';
import { Icon } from '@shopify/polaris';
import { CheckIcon, SearchIcon } from '@shopify/polaris-icons';
import i18n from 'src/locales/i18n';
import { cleanParams } from 'src/utils/url';
import { inputStyles, errorInputBgStyles } from 'src/utils/style';
import { isObject } from 'src/utils/type_check';

const defaultFilterOptions = createFilterOptions();

export function RHFAPIAutocompleteV2({
  name,
  displayName,
  displayNameVariant = 'body_md',
  params,
  helperText,
  searchParamName,
  useOptions,
  toOption,
  onClick,
  onChange = () => {},
  disabled,
  placeholder,
  onChangeWithValue = false,
  resetSearchOnOpen = false,
  hideSearchIcon = false,
  renderOptionContent,
  initialText,
  ...others
}) {
  const { control, getValues } = useFormContext();

  const [text, setText] = useState(initialText ?? '');
  const [open, setOpen] = useState(false);
  const [userTyped, setUserTyped] = useState(false);
  const debounceText = useDebounce(text);

  useEffect(() => {
    if (initialText) setText(initialText);
  }, [initialText]);

  const debounceParams = useMemo(
    () => ({
      ...params,
      [searchParamName]: resetSearchOnOpen && open && !userTyped ? '' : debounceText,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounceText, params, resetSearchOnOpen, open, userTyped],
  );

  const { searchResults } = useOptions(cleanParams(debounceParams));
  const options = useMemo(() => {
    return searchResults.map((r) => toOption(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);
  const value = useWatch({ control, name }) ?? '';
  useEffect(() => {
    if (value === '') {
      setText('');
    }
  }, [value]);

  useEffect(() => {
    if (value === '' || open) return;
    const label = options.find((x) => x.value === value)?.label || '';
    if (label && label !== text) {
      setText(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  const CustomPaper = (props) => {
    return (
      <Paper
        sx={{
          borderRadius: '12px',
          boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          marginTop: '6px',
          '& .MuiAutocomplete-listbox': {
            paddingY: '0px',
          },
        }}
        {...props}
      />
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box sx={{ width: '100%' }}>
          {displayName && (
            <InputLabel sx={{ color: 'text.primary' }}>
              <Typography
                variant={displayNameVariant}
                sx={{ color: 'text.primary' }}
              >
                {displayName}
              </Typography>
            </InputLabel>
          )}
          <Autocomplete
            {...field}
            value={field.value === '' ? null : field.value}
            inputValue={text}
            size="small"
            clearOnBlur={false}
            open={resetSearchOnOpen ? open : undefined}
            filterOptions={(opts, state) =>
              resetSearchOnOpen && open && !userTyped ? opts : defaultFilterOptions(opts, state)
            }
            onOpen={() => {
              setOpen(true);
              setUserTyped(false);
            }}
            onClose={() => {
              setOpen(false);
              setUserTyped(false);
              if (resetSearchOnOpen) {
                const selectedLabel = options.find((x) => x.value === getValues(name))?.label || '';
                setText(selectedLabel);
              }
            }}
            onInputChange={(e, val) => {
              if (e) {
                setText(val);
                setUserTyped(true);
              }
            }}
            onChange={(e, val) => {
              if (!val || field.value !== val.value) {
                onChangeWithValue ? onChange(val) : onChange();
              }
              if (!val) {
                setText('');
                field.onChange('');
              } else {
                setText(val.label);
                field.onChange(val.value);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ ...inputStyles, ...errorInputBgStyles }}
                error={!!error}
                placeholder={placeholder ?? i18n.t('common:message.searching')}
                helperText={
                  error ? (
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <ErrorOutlineOutlinedIcon sx={{ fontSize: '0.875rem' }} />
                      <span>{error?.message}</span>
                    </Box>
                  ) : (
                    helperText
                  )
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: hideSearchIcon ? (
                    params.InputProps?.startAdornment
                  ) : (
                    <Icon source={SearchIcon} />
                  ),
                }}
                variant={disabled ? 'filled' : 'outlined'}
                onClick={onClick}
              />
            )}
            options={options}
            isOptionEqualToValue={(option, value) => option.value === value}
            getOptionLabel={(option) => (isObject(option) ? option.label : '')}
            PaperComponent={CustomPaper}
            renderOption={(props, option) => (
              <Box
                {...props}
                key={props.id}
                sx={{
                  '&[aria-selected="true"]': {
                    borderRadius: '8px !important',
                    backgroundColor: 'rgba(235, 235, 235, 1)',
                  },
                  '&.MuiAutocomplete-option.Mui-focused': {
                    borderRadius: '8px',
                  },
                  '&:hover': {
                    borderRadius: '8px',
                    backgroundColor: 'rgba(241, 241, 241, 1) !important',
                  },
                  '&.MuiAutocomplete-option': {
                    paddingX: '6px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    borderRadius: '8px',
                  },

                  paddingX: '0px',
                  margin: '6px',
                }}
              >
                {renderOptionContent ? (
                  renderOptionContent(option, option.value === field.value)
                ) : (
                  <Typography
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    fontWeight={option.value === field.value ? '650' : '400'}
                  >
                    {option.label}
                  </Typography>
                )}
                {option.value === field.value && (
                  <CheckIcon
                    height={'20px'}
                    style={{ paddingLeft: '6px' }}
                  />
                )}
              </Box>
            )}
            disabled={disabled}
            {...others}
          />
        </Box>
      )}
    />
  );
}
